import React, { useRef, useEffect } from 'react';

interface RichTextDisplayProps {
  content: string;
  className?: string;
  maxLines?: number;
  enableShowMore?: boolean; // Kept for backward compatibility, now enables scrolling
}

/**
 * Component for safely displaying rich text content created with ReactQuill.
 * This component renders HTML content while maintaining security by only allowing
 * the basic formatting tags that ReactQuill produces (bold, italic, underline, lists).
 * Text content is scrollable when it exceeds the maximum height.
 */
const RichTextDisplay: React.FC<RichTextDisplayProps> = ({ 
  content, 
  className = "", 
  maxLines = 4,
  enableShowMore = true 
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // If no content, return null
  if (!content || !content.trim()) {
    return null;
  }

  // Calculate max height based on maxLines and enable scrolling
  useEffect(() => {
    if (contentRef.current && enableShowMore) {
      const element = contentRef.current;
      const lineHeight = parseInt(getComputedStyle(element).lineHeight) || 24;
      const maxHeight = lineHeight * maxLines;
      
      // Set max height to enable scrolling if content exceeds
      element.style.maxHeight = `${maxHeight}px`;
      element.style.overflowY = 'auto';
    } else if (contentRef.current && !enableShowMore) {
      // If scrolling is disabled, show full content
      contentRef.current.style.maxHeight = 'none';
      contentRef.current.style.overflowY = 'visible';
    }
  }, [content, maxLines, enableShowMore]);

  // Clean content by removing empty paragraphs and extra spacing
  const cleanContent = content
    .replace(/<p><br><\/p>/g, '')
    .replace(/<p>\s*<\/p>/g, '')
    .trim();

  return (
    <div className="rich-text-container">
      <div 
        ref={contentRef}
        className={`rich-text-content rich-text-scrollable ${className}`}
        dangerouslySetInnerHTML={{ __html: cleanContent }}
      />
    </div>
  );
};

export default RichTextDisplay;